import { FormControl, MenuItem, Select, type SelectChangeEvent } from '@mui/material';
import { type LanguageType } from '../../@types';
import { useAvailableLanguages } from '../../hooks/useAvailableLanguages';
import useLocalization from '../../hooks/useLocalization';
import { useLanguage } from '../../localization/LanguageInstanceProvider';

const styles = {
  root: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 72,
  },
  select: {
    borderRadius: 2,
    backgroundColor: 'action.hover',
    fontWeight: 700,
    '& .MuiSelect-select': {
      py: 0.75,
      pl: 1.25,
      pr: 3.5,
      textAlign: 'center',
    },
    '& .MuiSelect-icon': {
      right: 4,
    },
  },
} as const;

const KycLanguageSelect = () => {
  const { i18n, t } = useLocalization();
  const { setSelectedLanguage } = useLanguage();
  const availableLanguages = useAvailableLanguages();
  const label = t('language', 'Language');
  const fallbackLanguage = availableLanguages[0]?.key ?? '';
  const currentLanguage = i18n.resolvedLanguage ?? i18n.language;
  const value = availableLanguages.some((language) => language.key === currentLanguage)
    ? currentLanguage
    : fallbackLanguage;

  const handleChange = (event: SelectChangeEvent) => {
    void setSelectedLanguage(event.target.value as LanguageType);
  };

  if (availableLanguages.length < 2) {
    return null;
  }

  return (
    <FormControl fullWidth size='small' sx={styles.root} data-testid='kyc-language-select'>
      <Select
        id='kyc-language-select-input'
        inputProps={{ 'aria-label': label }}
        onChange={handleChange}
        renderValue={(selectedLanguage) => String(selectedLanguage).toUpperCase()}
        sx={styles.select}
        value={value}
      >
        {availableLanguages.map((language) => (
          <MenuItem key={language.key} value={language.key}>
            {language.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default KycLanguageSelect;
